import { useCallback } from 'react';
import axios from 'axios';
import useSWR, { useSWRConfig } from 'swr';
import { toast } from 'sonner';

import { formatAndThrowApiError } from '@/helpers/api';
import { useListEntrySwrConfig } from '@/helpers/_hooks';
import { fetchData } from '@/helpers/swr';

const agentConversationsKey = () => `/agent-conversation`;

// The caller's own conversations, most recent first.
const useAgentConversations = () => {
  const swr = useSWR(agentConversationsKey(), fetchData);
  return swr;
};

const useAgentConversationObject = (uid) => {
  const agentConversations = useSWR(agentConversationsKey(), fetchData, useListEntrySwrConfig(uid));
  return agentConversations.data.content[uid];
};

// One conversation with its messages and summaries of the records they link.
// Not refetched on focus: the panel shows a running reply from the stream and
// refetches once it is saved, so a focus refetch mid-reply would only flicker.
// `fetchData` so a failure carries `status`: the panel tells a deleted
// conversation (404) from any other error.
const useAgentConversation = (uid) => {
  const swr = useSWR(`/agent-conversation/${uid}`, fetchData, { revalidateOnFocus: false });
  return swr;
};

// Stable identity: the panel's send callback depends on it.
const usePostAgentConversation = () => {
  const postAgentConversation = useCallback(async (payload) => {
    try {
      const url = `/agent-conversation`;
      const result = await axios.post(url, payload);
      return result.data;
    } catch (error) {
      toast.error('An error occurred starting a conversation');
      formatAndThrowApiError(error);
    }
  }, []);

  return postAgentConversation;
};

const usePutAgentConversation = () => {
  const putAgentConversation = async (uid, payload) => {
    try {
      const url = `/agent-conversation/${uid}`;
      const result = await axios.put(url, payload);
      return result.data;
    } catch (error) {
      toast.error('An error occurred editing the conversation');
      formatAndThrowApiError(error);
    }
  };

  return putAgentConversation;
};

const useDeleteAgentConversation = () => {
  const deleteAgentConversation = async (uid) => {
    try {
      const url = `/agent-conversation/${uid}`;
      await axios.delete(url);
    } catch (error) {
      toast.error('An error occurred deleting the conversation');
      formatAndThrowApiError(error);
    }
  };

  return deleteAgentConversation;
};

// Refetches a conversation and the history list once a reply is saved — its
// title and messages changed on the server, not through a write here. Resolves
// with the conversation as it now stands.
// Stable identity: the panel's send callback depends on it.
const useRevalidateAgentConversation = () => {
  const { mutate } = useSWRConfig();
  const revalidateAgentConversation = useCallback(
    async (uid) => {
      const [conversation] = await Promise.all([mutate(`/agent-conversation/${uid}`), mutate(agentConversationsKey())]);
      return conversation;
    },
    [mutate]
  );

  return revalidateAgentConversation;
};

export {
  useAgentConversations,
  useAgentConversationObject,
  useAgentConversation,
  usePostAgentConversation,
  usePutAgentConversation,
  useDeleteAgentConversation,
  useRevalidateAgentConversation
};
